import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BlogPost } from '../../../mocks/blog';
import { getAuthorByName } from '../../../mocks/authors';

interface ArticleSidebarProps {
  post: BlogPost;
}

export default function ArticleSidebar({ post }: ArticleSidebarProps) {
  const [copied, setCopied] = useState(false);

  const h2Headings = post.content.filter((b) => b.type === 'h2') as { type: 'h2'; text: string }[];

  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollTo = (text: string) => {
    const headings = document.querySelectorAll('h2');
    headings.forEach((h) => {
      if (h.textContent && h.textContent.trim() === text.trim()) {
        h.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  return (
    <div className="space-y-6 sticky top-28">
      {/* Table of contents */}
      {h2Headings.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
          <p className="text-zinc-500 text-xs uppercase tracking-widest font-semibold mb-5">
            Obsah článku
          </p>
          <nav className="space-y-1">
            {h2Headings.map((heading, i) => (
              <button
                key={i}
                onClick={() => scrollTo(heading.text)}
                className="w-full text-left flex items-start gap-3 py-2 px-3 rounded-lg text-zinc-400 hover:text-orange-300 hover:bg-orange-500/5 transition-all duration-150 cursor-pointer group"
              >
                <span className="text-orange-500/50 text-xs font-mono mt-0.5 flex-shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span className="text-sm leading-snug group-hover:text-orange-300 transition-colors">
                  {heading.text}
                </span>
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Author card */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-semibold mb-5">
          O autorovi
        </p>
        <Link
          to={`/autor/${getAuthorByName(post.author)?.id ?? ''}`}
          className="flex items-center gap-4 mb-4 group cursor-pointer"
        >
          <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-orange-500/30 group-hover:border-orange-400 transition-colors duration-200 flex-shrink-0">
            <img
              src={post.authorImage}
              alt={post.author}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div>
            <p className="text-white font-bold text-sm group-hover:text-orange-300 transition-colors duration-200">{post.author}</p>
            <p className="text-zinc-500 text-xs mt-0.5">{post.authorRole}</p>
          </div>
        </Link>
        <p className="text-zinc-400 text-sm leading-relaxed mb-4">
          Špecialista na digitálny marketing s viac ako 4 rokmi skúseností. Pomáha slovenským firmám rásť online prostredníctvom SEO, platenej reklamy a webového dizajnu.
        </p>
        <Link
          to={`/autor/${getAuthorByName(post.author)?.id ?? ''}`}
          className="inline-flex items-center gap-1.5 text-orange-400 hover:text-orange-300 text-xs font-semibold transition-colors duration-200 cursor-pointer"
        >
          Zobraziť profil autora
          <i className="ri-arrow-right-line" />
        </Link>
      </div>

      {/* Share */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
        <p className="text-zinc-500 text-xs uppercase tracking-widest font-semibold mb-4">
          Zdieľať článok
        </p>
        <div className="flex gap-2">
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="nofollow noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-sm transition-all duration-200 cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-facebook-fill" />
            </div>
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`}
            target="_blank"
            rel="nofollow noreferrer"
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white text-sm transition-all duration-200 cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-linkedin-fill" />
            </div>
          </a>
          <button
            onClick={handleCopy}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm transition-all duration-200 cursor-pointer ${
              copied
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-white'
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={copied ? 'ri-check-line' : 'ri-link'} />
            </div>
            <span className="text-xs">{copied ? 'Skopírované' : 'Kopírovať'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
